import { useState } from "react";
import { BrandLockup } from "@/shell/CandidateFlow";
import { Link } from "react-router-dom";

export function Landing() {
  const [name, setName] = useState("");
  const [company, setCompany] = useState("");
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState<"idle" | "loading" | "done" | "error">("idle");
  const [errorMsg, setErrorMsg] = useState("");

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!name.trim()) {
      setErrorMsg("Please enter your name.");
      return;
    }
    if (!email || !email.includes("@")) {
      setErrorMsg("Please enter a valid email address.");
      return;
    }
    setStatus("loading");
    setErrorMsg("");
    try {
      const csrfRes = await fetch("/api/v1/csrf-token", { credentials: "include" });
      const { token: csrfToken } = await csrfRes.json();
      const res = await fetch("/api/v1/beta-signup", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json", "X-CSRFToken": csrfToken },
        body: JSON.stringify({ email, name: name.trim(), firm: company.trim() }),
      });
      const data = await res.json();
      if (data.ok) {
        setStatus("done");
      } else {
        setStatus("error");
        setErrorMsg(data.error ?? "Something went wrong. Please try again.");
      }
    } catch {
      setStatus("error");
      setErrorMsg("Network error. Please try again.");
    }
  }

  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        background: "#FBFAFE",
        fontFamily: "'Inter', system-ui, sans-serif",
      }}
    >
      <div
        style={{
          flex: 1,
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          minHeight: "100vh",
          minWidth: 0,
        }}
        className="landing-grid"
      >
        {/* ── Left panel ── */}
        <div
          style={{
            background: "#FBFAFE",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            padding: "64px 56px",
          }}
          className="landing-left"
        >
          {/* Wordmark + sign in — a returning recruiter has no other way to
              find /auth/login from this page otherwise. */}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 36 }}>
            <BrandLockup />
            <Link
              to="/auth/login"
              style={{ fontSize: 13, fontWeight: 500, color: "#7A6BE0", textDecoration: "none" }}
            >
              Sign in
            </Link>
          </div>

          {/* Eyebrow */}
          <div
            style={{
              fontFamily: "ui-monospace, monospace",
              fontSize: 10,
              fontWeight: 600,
              letterSpacing: "0.12em",
              textTransform: "uppercase" as const,
              color: "#7C7590",
              marginBottom: 20,
            }}
          >
            White-label AI interview preparation
          </div>

          {/* Hero */}
          <h1
            style={{
              fontSize: "clamp(30px, 3.8vw, 46px)",
              fontWeight: 800,
              lineHeight: 1.1,
              color: "#17131F",
              marginBottom: 24,
              letterSpacing: "-0.02em",
              maxWidth: 460,
            }}
          >
            Turn candidate preparation into a repeatable advantage.
          </h1>

          <p
            style={{
              fontSize: 15,
              lineHeight: 1.7,
              color: "#4A4260",
              marginBottom: 36,
              maxWidth: 400,
            }}
          >
            Executive Interview Coach gives senior candidates a voice-led practice
            interview built around their CV, the role brief and the hiring
            context. Your firm sends the link, the candidate practises, and your
            consultant gets structured feedback before the client interview.
          </p>

          {/* Form or success */}
          {status === "done" ? (
            <div
              style={{
                background: "rgba(122,107,224,0.06)",
                border: "1px solid rgba(122,107,224,0.20)",
                borderRadius: 8,
                padding: "24px 28px",
                maxWidth: 420,
              }}
            >
              <div
                style={{
                  fontFamily: "ui-monospace, monospace",
                  fontSize: 11,
                  fontWeight: 600,
                  letterSpacing: "0.10em",
                  textTransform: "uppercase" as const,
                  color: "#7A6BE0",
                  marginBottom: 8,
                }}
              >
                You're on the list
              </div>
              <p style={{ fontSize: 14, color: "#4A4260", lineHeight: 1.6, margin: 0 }}>
                We'll be in touch shortly. In the meantime, feel free to reach out at{" "}
                <a href="mailto:hello@executiveinterview.coach" style={{ color: "#7A6BE0", textDecoration: "none" }}>
                  hello@executiveinterview.coach
                </a>
                .
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} style={{ maxWidth: 420 }}>
              <div style={{ display: "flex", gap: 10, marginBottom: 10 }}>
                <input
                  type="text"
                  placeholder="Your name"
                  value={name}
                  required
                  onChange={(e) => setName(e.target.value)}
                  style={{
                    flex: 1,
                    padding: "13px 16px",
                    border: "1px solid #9A93AD",
                    borderRadius: 8,
                    fontSize: 14,
                    color: "#17131F",
                    background: "#FFFFFF",
                    outline: "none",
                    fontFamily: "'Inter', system-ui, sans-serif",
                    minWidth: 0,
                  }}
                />
                <input
                  type="text"
                  placeholder="Firm name"
                  value={company}
                  onChange={(e) => setCompany(e.target.value)}
                  style={{
                    flex: 1,
                    padding: "13px 16px",
                    border: "1px solid #9A93AD",
                    borderRadius: 8,
                    fontSize: 14,
                    color: "#17131F",
                    background: "#FFFFFF",
                    outline: "none",
                    fontFamily: "'Inter', system-ui, sans-serif",
                    minWidth: 0,
                  }}
                />
              </div>
              <div
                style={{
                  display: "flex",
                  borderRadius: 8,
                  overflow: "hidden",
                  border: "1px solid #9A93AD",
                  marginBottom: errorMsg ? 8 : 12,
                }}
              >
                <input
                  type="email"
                  placeholder="name@yourfirm.com"
                  value={email}
                  required
                  onChange={(e) => setEmail(e.target.value)}
                  style={{
                    flex: 1,
                    padding: "13px 16px",
                    border: "none",
                    fontSize: 14,
                    color: "#17131F",
                    background: "#FFFFFF",
                    outline: "none",
                    fontFamily: "'Inter', system-ui, sans-serif",
                    minWidth: 0,
                  }}
                />
                <button
                  type="submit"
                  disabled={status === "loading"}
                  style={{
                    padding: "13px 20px",
                    background: status === "loading" ? "#7C7590" : "#7A6BE0",
                    color: "#fff",
                    border: "none",
                    fontFamily: "'Inter', system-ui, sans-serif",
                    fontSize: 14,
                    fontWeight: 500,
                    cursor: status === "loading" ? "not-allowed" : "pointer",
                    whiteSpace: "nowrap" as const,
                    transition: "background 0.15s",
                  }}
                >
                  {status === "loading" ? "Applying…" : "Apply for the beta →"}
                </button>
              </div>
              {errorMsg && (
                <p style={{ fontSize: 13, color: "#c0392b", marginBottom: 8 }}>{errorMsg}</p>
              )}
              <p style={{ fontSize: 12, color: "#7C7590", lineHeight: 1.5 }}>
                By applying you agree to our{" "}
                <Link to="/terms" style={{ color: "#7A6BE0", textDecoration: "none" }}>Terms</Link>{" "}
                and{" "}
                <Link to="/privacy" style={{ color: "#7A6BE0", textDecoration: "none" }}>Privacy Policy</Link>.
              </p>
            </form>
          )}

          {/* Footnote */}
          <div
            style={{
              fontFamily: "ui-monospace, monospace",
              fontSize: 9,
              fontWeight: 600,
              letterSpacing: "0.10em",
              textTransform: "uppercase" as const,
              color: "#8C86A0",
              marginTop: 44,
              maxWidth: 380,
              lineHeight: 1.7,
            }}
          >
            Built for boutique executive search firms placing Director, VP and C-suite talent.
          </div>
        </div>

        {/* ── Right panel — portal preview ── */}
        <div
          style={{
            background: "linear-gradient(155deg, #EFEBFF 0%, #F5E6F7 52%, #FCEFEA 100%)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "48px 40px",
            position: "relative",
            overflow: "hidden",
          }}
          className="landing-right"
        >
          {/* Subtle dot grid */}
          <div
            style={{
              position: "absolute",
              inset: 0,
              backgroundImage: "radial-gradient(rgba(122,107,224,0.18) 1px, transparent 1px)",
              backgroundSize: "28px 28px",
              pointerEvents: "none",
            }}
          />

          {/* Mock browser */}
          <div
            style={{
              width: "100%",
              maxWidth: 380,
              borderRadius: 10,
              overflow: "hidden",
              boxShadow: "0 30px 80px rgba(27,26,36,0.16), 0 8px 24px rgba(27,26,36,0.08)",
              position: "relative",
              zIndex: 1,
            }}
          >
            {/* Browser chrome */}
            <div
              style={{
                background: "#EAE4F3",
                padding: "9px 12px",
                display: "flex",
                alignItems: "center",
                gap: 7,
              }}
            >
              <div style={{ display: "flex", gap: 5 }}>
                {["#FF5F57", "#FFBD2E", "#28C840"].map((c) => (
                  <div key={c} style={{ width: 9, height: 9, borderRadius: "50%", background: c, opacity: 0.7 }} />
                ))}
              </div>
              <div
                style={{
                  flex: 1,
                  marginLeft: 6,
                  background: "rgba(0,0,0,0.08)",
                  borderRadius: 4,
                  padding: "3px 10px",
                  fontFamily: "ui-monospace, monospace",
                  fontSize: 9,
                  color: "rgba(0,0,0,0.40)",
                  letterSpacing: "0.01em",
                  userSelect: "none" as const,
                }}
              >
                acmesearch.executiveinterview.coach
              </div>
            </div>

            {/* Portal content */}
            <div style={{ background: "#FBFAFE", padding: "32px 28px", textAlign: "center" }}>
              {/* Agency lockup */}
              <div style={{ marginBottom: 28 }}>
                <div
                  style={{
                    fontFamily: "ui-monospace, monospace",
                    fontSize: 10,
                    fontWeight: 700,
                    letterSpacing: "0.14em",
                    textTransform: "uppercase" as const,
                    color: "#17131F",
                    marginBottom: 6,
                  }}
                >
                  Acme Executive Search
                </div>
                <div
                  style={{
                    fontFamily: "ui-monospace, monospace",
                    fontSize: 8,
                    letterSpacing: "0.12em",
                    textTransform: "uppercase" as const,
                    color: "#8C86A0",
                    marginBottom: 6,
                  }}
                >
                  Powered by
                </div>
                <div style={{ display: "flex", justifyContent: "center" }}>
                  <BrandLockup />
                </div>
              </div>

              {/* Welcome card */}
              <h2
                style={{
                  fontSize: 18,
                  fontWeight: 700,
                  color: "#17131F",
                  marginBottom: 8,
                  letterSpacing: "-0.01em",
                }}
              >
                Welcome, Jordan.
              </h2>
              <p style={{ fontSize: 13, color: "#4A4260", lineHeight: 1.55, marginBottom: 24 }}>
                Your practice interview for the CMO role is ready — about 25 minutes.
              </p>

              {/* CTA */}
              <div
                style={{
                  background: "#7A6BE0",
                  borderRadius: 6,
                  padding: "12px 24px",
                  color: "#fff",
                  fontFamily: "'Inter', system-ui, sans-serif",
                  fontSize: 14,
                  fontWeight: 500,
                  marginBottom: 20,
                  cursor: "pointer",
                }}
              >
                Get started →
              </div>

              {/* Tagline */}
              <div
                style={{
                  fontFamily: "ui-monospace, monospace",
                  fontSize: 8,
                  fontWeight: 600,
                  letterSpacing: "0.14em",
                  textTransform: "uppercase" as const,
                  color: "#9A93AD",
                }}
              >
                Your brand · Our intelligence
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer
        style={{
          background: "#FBFAFE",
          borderTop: "1px solid #EFEBF7",
          padding: "20px 56px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 16,
        }}
        className="landing-footer"
      >
        <span style={{ fontFamily: "ui-monospace, monospace", fontSize: 10, color: "#8C86A0", letterSpacing: "0.06em" }}>
          © 2026 Executive Interview Coach
        </span>
        <div style={{ display: "flex", gap: 20 }}>
          <Link to="/terms" style={{ fontFamily: "ui-monospace, monospace", fontSize: 10, color: "#7C7590", textDecoration: "none", letterSpacing: "0.06em" }}>
            Terms
          </Link>
          <Link to="/privacy" style={{ fontFamily: "ui-monospace, monospace", fontSize: 10, color: "#7C7590", textDecoration: "none", letterSpacing: "0.06em" }}>
            Privacy
          </Link>
        </div>
      </footer>

      <style>{`
        @media (max-width: 768px) {
          .landing-grid { grid-template-columns: 1fr !important; min-width: 0; }
          .landing-left { padding: 40px 24px !important; justify-content: flex-start !important; padding-top: 48px !important; min-width: 0; overflow: hidden; }
          .landing-right { min-height: 400px; padding: 40px 24px !important; min-width: 0; overflow: hidden; }
          .landing-footer { padding: 20px 24px !important; flex-direction: column; align-items: flex-start !important; }
        }
      `}</style>
    </div>
  );
}
